import React, { useState } from "react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar, Clock, Hash, Phone } from "lucide-react";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  DismissibleChip,
  ChipContainer,
} from "@/components/consultation/shared/DismissibleChip";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { ExpandedCardDialog } from "@/components/consultation/dialogs/ExpandedCardDialog";
import type {
  PatientInfo,
  PatientConcern,
  PatientSurgery,
} from "@/types/appointments";
import { useConsultationEchoSession } from "@/hooks/useConsultationEchoSession";
import { useDeletePatientSurgery } from "@/hooks/useConsultation";
import { VideoCallView } from "@/components/video-call/VideoCallView";
import { HealthDataPanel } from "./HealthDataPanel";
import { PatientInfoCompactCard } from "./PatientInfoCompactCard";
import { RiskAnalysisCard, type RiskItem } from "./RiskAnalysisCard";

const formatStartTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "MMM d, h:mm a");
};

interface PatientContextPanelProps {
  patientInfo?: PatientInfo;
  patientConcerns: PatientConcern[];
  patientSurgeries: PatientSurgery[];
  activeAppointmentId?: string;
  onManageConcern: () => void;
  onManageSurgery: () => void;
  hideHistoryAndConcerns?: boolean;
  showHealthData?: boolean;
  isCompleted?: boolean;
  riskAnalysis?: RiskItem[] | null;
  zarityAppointmentId?: string;
  appointmentStartTime?: string;
  appointmentDuration?: string;
}

export const PatientContextPanel = ({
  patientInfo,
  patientConcerns,
  patientSurgeries,
  activeAppointmentId,
  onManageConcern,
  onManageSurgery,
  hideHistoryAndConcerns = false,
  showHealthData = false,
  isCompleted = false,
  riskAnalysis,
  zarityAppointmentId,
  appointmentStartTime,
  appointmentDuration,
}: PatientContextPanelProps) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [expandedSection, setExpandedSection] = useState<
    "concern" | "surgery" | null
  >(null);
  const deleteSurgery = useDeletePatientSurgery();
  const consultationSession = useConsultationEchoSession({
    appointmentId: activeAppointmentId,
  });

  const handleMaximize = () => setIsMaximized(true);
  const handleMinimize = () => setIsMaximized(false);

  const filteredConcerns = patientConcerns
    .filter(
      (concernGroup) =>
        concernGroup.appointment === activeAppointmentId || !activeAppointmentId
    )
    .flatMap((concernGroup) =>
      concernGroup.concerns.map((concern) => ({
        concern,
        key: `${concernGroup.id}-${concern}`,
      }))
    );

  const initials = patientInfo?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4 scrollbar-thin scrollbar-stable">
      {/* Patient Identity / Video Call */}
      {consultationSession.isVideoCallActive && activeAppointmentId ? (
        <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden bg-transparent">
          <VideoCallView
            isConnecting={consultationSession.isConnecting}
            isVideoEnabled={consultationSession.isVideoEnabled}
            isAudioEnabled={consultationSession.isAudioEnabled}
            isMuted={consultationSession.isMuted}
            isCallEnded={consultationSession.sessionStatus === "ended"}
            localUserJoined={consultationSession.localUserJoined}
            remoteUserJoined={consultationSession.remoteUserJoined}
            networkQuality={consultationSession.networkQuality}
            callDuration={consultationSession.callDuration}
            error={consultationSession.error}
            initError={consultationSession.initError}
            isInitializing={consultationSession.isInitializing}
            isRejoining={consultationSession.isRejoining}
            localVideoRef={consultationSession.localVideoRef}
            remoteVideoRef={consultationSession.remoteVideoRef}
            onToggleMute={consultationSession.toggleMute}
            onToggleVideo={consultationSession.toggleVideo}
            onToggleAudio={consultationSession.toggleAudio}
            onEndCall={consultationSession.endConsultation}
            onRejoin={consultationSession.rejoinConsultation}
            // onNotifyPatient={consultationSession.notifyPatientToJoin} // TODO: enable in next version
            patientName={patientInfo?.full_name}
            isMaximized={isMaximized}
            compact={!isMaximized}
            onMaximize={handleMaximize}
            onMinimize={handleMinimize}
            className="relative w-full"
          />
        </Card>
      ) : (
        <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden bg-card">
          <div className="flex min-h-80 w-full flex-col items-center justify-center gap-4 p-4">
            <div className="relative">
              <Avatar className="h-16 w-16 border-2 border-muted-foreground/20 shadow-lg ring-4 ring-muted/60">
                <AvatarFallback className="bg-card text-foreground z-body-md font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-card bg-primary shadow-sm" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <p className="z-body-md text-foreground font-semibold">
                {patientInfo?.full_name}
              </p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {zarityAppointmentId && (
                  <Badge
                    variant="secondary"
                    className="z-caption tabular-nums h-5 px-2 border-0 gap-1"
                  >
                    <Hash className="size-3" />
                    {zarityAppointmentId}
                  </Badge>
                )}
                {appointmentStartTime && (
                  <Badge
                    variant="secondary"
                    className="z-caption h-5 px-2 border-0 gap-1"
                  >
                    <Calendar className="size-3" />
                    {formatStartTime(appointmentStartTime)}
                  </Badge>
                )}
                {appointmentDuration && (
                  <Badge
                    variant="secondary"
                    className="z-caption h-5 px-2 border-0 gap-1"
                  >
                    <Clock className="size-3" />
                    {appointmentDuration} min
                  </Badge>
                )}
              </div>
            </div>
            <Button
              onClick={() => void consultationSession.startConsultation()}
              className="h-9 px-6 rounded-xl"
            >
              <Phone className="size-3.5 mr-1.5" />
              Start Consultation
            </Button>
          </div>
        </Card>
      )}

      {/* Patient Info — compact identity row (visit type, age/sex, BMI, speaks) */}
      <PatientInfoCompactCard patientInfo={patientInfo} />

      {/* Risk Analysis */}
      <RiskAnalysisCard riskAnalysis={riskAnalysis} />

      {/* Health Data (Nutrition Evaluation) */}
      {showHealthData && activeAppointmentId && (
        <HealthDataPanel appointmentId={activeAppointmentId} />
      )}

      {/* Patient Concern */}
      {!hideHistoryAndConcerns && !showHealthData && (
        <Widget>
          <WidgetHeader
            title="Patient Concern"
            hasInfo
            onMaximize={
              filteredConcerns.length > 0
                ? () => setExpandedSection("concern")
                : undefined
            }
            onAdd={isCompleted ? undefined : onManageConcern}
          />
          <WidgetContent>
            {filteredConcerns.length > 0 ? (
              <div className="max-h-[160px] divide-y divide-border/60 overflow-y-auto scrollbar-thin">
                {filteredConcerns.map(({ concern, key }, idx) => (
                  <div
                    key={key}
                    className="flex items-start gap-2 px-0.5 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="z-secondary text-muted-foreground shrink-0">
                      {idx + 1}.
                    </span>
                    <p className="z-secondary text-foreground leading-snug">
                      {concern}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No concerns reported" variant="compact" />
            )}
          </WidgetContent>
        </Widget>
      )}

      {/* Surgical History — follows FamilyHistoryCard chip style */}
      {!hideHistoryAndConcerns && !showHealthData && (
        <Widget>
          <WidgetHeader
            title="Surgical History"
            hasInfo
            onMaximize={
              patientSurgeries.length > 0
                ? () => setExpandedSection("surgery")
                : undefined
            }
            onAdd={isCompleted ? undefined : onManageSurgery}
          />
          <WidgetContent>
            {patientSurgeries.length > 0 ? (
              <ChipContainer>
                {patientSurgeries.map((s) => (
                  <DismissibleChip key={s.id} label={s.surgery_details.name} />
                ))}
              </ChipContainer>
            ) : (
              <EmptyState title="No surgeries recorded" variant="compact" />
            )}
          </WidgetContent>
        </Widget>
      )}

      {/* Expanded view dialogs */}
      <ExpandedCardDialog
        open={expandedSection === "concern"}
        onOpenChange={(open) => !open && setExpandedSection(null)}
        title="Patient Concern"
      >
        <div className="space-y-2">
          {filteredConcerns.map(({ concern, key }, idx) => (
            <div
              key={key}
              className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/30 px-4 py-3"
            >
              <span className="z-body-md text-muted-foreground shrink-0">
                {idx + 1}.
              </span>
              <p className="z-body text-foreground leading-relaxed">
                {concern}
              </p>
            </div>
          ))}
        </div>
      </ExpandedCardDialog>

      <ExpandedCardDialog
        open={expandedSection === "surgery"}
        onOpenChange={(open) => !open && setExpandedSection(null)}
        title="Surgical History"
      >
        <ChipContainer className="max-h-none">
          {patientSurgeries.map((s) => (
            <DismissibleChip
              key={s.id}
              label={s.surgery_details.name}
              onRemove={
                isCompleted || !activeAppointmentId
                  ? undefined
                  : () =>
                      deleteSurgery.mutate({
                        appointmentId: activeAppointmentId,
                        surgeryId: String(s.id),
                      })
              }
            />
          ))}
        </ChipContainer>
      </ExpandedCardDialog>
    </div>
  );
};
